import React, { useMemo } from 'react';
import { isNil } from 'lodash';
import { Stack, type SxProps } from '@mui/material';
import classNames from 'classnames';

import { useDayNightShadingBackground } from 'components/DayNightShading';
import GraphTimeline from 'components/GraphTimeline';
import { TimeTickColor } from 'components/GraphTimeline/GraphTimeline';
import { useMaxWidthTablet } from 'hooks/useMediaQueries';
import type { SunlightTimes } from 'types/sunlightTimes';
import { TideData, TidePoint, MoveTideLabel } from 'types/tide';
import { convertToMilliseconds, formatTimestampInTimezone } from 'utils/time';
import TideChart from '../TideChart';

import styles from './TideChartContainer.module.scss';

interface Props {
  tide: TideData;
  sunlightTimes: SunlightTimes;
  flatData: Array<TidePoint>;
  startTimestamp: number;
  endTimestamp: number;
  dateFormat?: 'MDY' | 'DMY';
  dayIndex?: number;
  tableModeGraphHeight?: number;
  isTableMode?: boolean;
  showNowPosition?: boolean;
  moveTideLabel: MoveTideLabel;
  customCssModuleWrapper?: string;
}

const TideChartContainer: React.FC<Props> = ({
  tide,
  sunlightTimes,
  flatData,
  startTimestamp,
  endTimestamp,
  dateFormat = 'MDY',
  isTableMode = false,
  dayIndex = 0,
  tableModeGraphHeight = 125,
  showNowPosition = true,
  moveTideLabel,
  customCssModuleWrapper = null,
}) => {
  const isMobile = useMaxWidthTablet();

  const timelineDays = useMemo(
    () =>
      tide?.days?.map((tideDay) =>
        tideDay?.filter(
          // For the timeline we want 12am to 11pm, so we'll filter out High and Low data points
          (tideDataPoint, index) => {
            if (index === tideDay.length - 1) {
              // and we also need remove midnight on the end of each day if exists
              return (
                formatTimestampInTimezone(tideDataPoint.timestamp, tideDataPoint.utcOffset, 'H') !==
                '0'
              );
            }
            return tideDataPoint.type === 'NORMAL';
          },
        ),
      ),
    [tide],
  );

  const timelineDateFormat = useMemo(
    () => (isTableMode && !isMobile ? 'haaa' : 'h'),
    [isTableMode, isMobile],
  );

  const stackStyle = useMemo<SxProps>(
    () =>
      isTableMode
        ? {
            flexDirection: 'row',
            height: 'unset',
            marginTop: '10px',
            paddingTop: '0',
          }
        : null,
    [isTableMode],
  );

  const { background } = useDayNightShadingBackground({ sunlightTimes, dayIndex });

  const shadingStyle = useMemo(
    () => ({
      background,
      ...(isTableMode ? { height: tableModeGraphHeight } : {}),
    }),
    [background, isTableMode, tableModeGraphHeight],
  );

  if (isNil(tide?.tideLocation) || isNil(tide?.units)) return null;

  return (
    <div>
      <Stack
        className={classNames(styles.wrapper, customCssModuleWrapper)}
        key={dayIndex}
        sx={stackStyle}
      >
        <div
          className={styles.canvas}
          data-testid="tide-chart-container-with-day-night-shading"
          style={shadingStyle}
        >
          <TideChart
            dateFormat={dateFormat}
            data={flatData}
            tideLocation={tide?.tideLocation}
            units={tide?.units?.tideHeight}
            startDate={convertToMilliseconds(startTimestamp)}
            endDate={convertToMilliseconds(endTimestamp)}
            moveTideLabel={moveTideLabel}
            dayIndex={dayIndex}
            isTableMode={isTableMode}
            showNowPosition={showNowPosition}
          />
        </div>
        {timelineDays?.[dayIndex] && (
          <div className={styles.graphTimelineWrapper}>
            <GraphTimeline
              customDateFormat={timelineDateFormat}
              day={timelineDays[dayIndex] as any}
              timeTickColor={TimeTickColor.DARK}
              alignStartOnMidnight
              showMarkers
            />
          </div>
        )}
      </Stack>
    </div>
  );
};

export default TideChartContainer;
